import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AudioLayout from "../AudioLayout";
import { audioApi } from "../api";
import { useAudioAuth } from "../AudioAuthContext";
import { useAudioAccess } from "../AudioAccessContext";
import PlayPaywallModal from "../components/PlayPaywallModal";
import usePlayPaywall from "../usePlayPaywall";
import { Lock, PlayCircle, Sparkles } from "lucide-react";

export default function AudioBooks() {
  const { subscribed } = useAudioAuth();
  const access = useAudioAccess();
  const [books, setBooks] = useState([]);
  const paywall = usePlayPaywall({ defaultNext: "/audio-comics/books" });
  // In FREE mode there are no locked badges and no upsell CTA.
  const canPlayDirectly = access.is_free || subscribed;
  const showUpsell = !access.is_free && !subscribed;

  useEffect(() => { audioApi.get("/books").then(r => setBooks(r.data)).catch(() => {}); }, []);

  return (
    <AudioLayout>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-amber-300 mb-2">Library</div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">All books</h1>
          <p className="text-white/60 text-sm mt-1">{books.length} books · {books.reduce((n, b) => n + (b.vachana_count || 0), 0)} vachanas</p>
        </div>
        {showUpsell && (
          <Link
            to="/audio-comics/subscription"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-400 text-black font-semibold hover:bg-amber-300 text-sm"
            data-testid="ac-books-cta-pro"
          >
            <Sparkles size={14}/> {access.cta_secondary}
          </Link>
        )}
      </div>

      {access.is_free && access.launch_offer_enabled && (
        <div
          className="rounded-2xl border border-emerald-400/30 bg-emerald-400/[0.06] p-5 mb-6 text-sm text-white/80 flex items-center gap-3"
          data-testid="ac-books-free-banner"
        >
          <Sparkles size={14} className="text-emerald-300"/>
          <span><b className="text-emerald-300">FREE</b> — {access.launch_offer_text}. Tap any book to listen.</span>
        </div>
      )}

      {showUpsell && (
        <div
          className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 mb-6 text-sm text-white/70 flex items-center gap-3"
          data-testid="ac-books-preview-banner"
        >
          <Sparkles size={14} className="text-amber-300"/>
          <span>Explore all Bala Basava Audio Comics before subscribing — tap any book to preview its vachanas.</span>
        </div>
      )}

      {books.length === 0 ? (
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-10 text-center text-white/60" data-testid="ac-empty-library">
          No audio comics available yet.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
          {books.map((b) => (
            <Link key={b.book_id} to={`/audio-comics/books/${b.book_id}`} className="group" data-testid={`ac-book-${b.book_id}`}>
              <div className="aspect-[3/4] rounded-xl overflow-hidden bg-white/5 ring-1 ring-white/10 group-hover:ring-amber-400/50 transition relative">
                {b.thumbnail_url ? (
                  <img src={b.thumbnail_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition duration-500"/>
                ) : (
                  <div className="grid place-items-center h-full text-white/40"><PlayCircle size={32}/></div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent"/>
                {!canPlayDirectly && (
                  <span className="absolute top-2 right-2 inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-black/60 backdrop-blur text-white/80 border border-white/10">
                    <Lock size={10}/> Pro
                  </span>
                )}
                {access.is_free && (
                  <span className="absolute top-2 right-2 inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/70 text-black font-semibold">
                    FREE
                  </span>
                )}
                <div className="absolute bottom-2 left-2 right-2">
                  <div className="text-[10px] text-amber-300 uppercase tracking-widest">{b.vachana_count} vachanas</div>
                  <div className="text-sm font-medium leading-tight line-clamp-2">{b.title}</div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      <PlayPaywallModal
        open={paywall.open}
        onClose={paywall.close}
        onSignIn={paywall.goSignIn}
        onSubscribe={paywall.goSubscribe}
      />
    </AudioLayout>
  );
}
